import * as React from "react"
import { Popover as PopoverPrimitive } from "radix-ui"
import { HugeiconsIcon } from "@hugeicons/react"
import { UnfoldMoreIcon, Tick02Icon, Search01Icon } from "@hugeicons/core-free-icons"

import { cn } from "@/lib/utils"
import { useT } from "@/app/localization"

export interface SearchableSelectOption {
  value: string
  label: string
}

interface SearchableSelectProps {
  options: SearchableSelectOption[]
  value?: string | null
  onValueChange: (value: string) => void
  placeholder?: string
  searchPlaceholder?: string
  emptyText?: string
  /** Extra classes for the trigger (e.g. width). */
  className?: string
  disabled?: boolean
}

/**
 * A single-select dropdown with a built-in search box, for long lists (e.g. the
 * organization picker) where the plain <Select> is unusable. Options are sorted
 * alphabetically by label and filtered case-insensitively as you type.
 */
export function SearchableSelect({
  options,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyText,
  className,
  disabled,
}: SearchableSelectProps) {
  const t = useT()
  const [open, setOpen] = React.useState(false)
  const [query, setQuery] = React.useState("")
  const inputRef = React.useRef<HTMLInputElement>(null)

  const sorted = React.useMemo(
    () =>
      [...options].sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: "base", numeric: true })
      ),
    [options]
  )
  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter((o) => o.label.toLowerCase().includes(q))
  }, [sorted, query])

  const selected = options.find((o) => o.value === value) ?? null

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(o) => {
        setOpen(o)
        if (!o) setQuery("")
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          type="button"
          disabled={disabled}
          data-slot="searchable-select-trigger"
          className={cn(
            "flex h-9 items-center justify-between gap-1.5 rounded-4xl border border-input bg-input/30 px-3 py-2 text-sm whitespace-nowrap transition-colors outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-input/50",
            className
          )}
        >
          <span className={cn("line-clamp-1 text-left", !selected && "text-muted-foreground")}>
            {selected ? selected.label : (placeholder ?? t("common.select.placeholder"))}
          </span>
          <HugeiconsIcon
            icon={UnfoldMoreIcon}
            strokeWidth={2}
            className="pointer-events-none size-4 shrink-0 text-muted-foreground"
          />
        </button>
      </PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
            inputRef.current?.focus()
          }}
          className="z-50 w-(--radix-popover-trigger-width) min-w-56 overflow-hidden rounded-2xl bg-popover text-popover-foreground shadow-2xl ring-1 ring-foreground/5 data-open:animate-in data-open:fade-in-0 data-open:zoom-in-95 data-closed:animate-out data-closed:fade-out-0 data-closed:zoom-out-95"
        >
          <div className="flex items-center gap-2 border-b border-border/50 px-3">
            <HugeiconsIcon
              icon={Search01Icon}
              strokeWidth={2}
              className="size-4 shrink-0 text-muted-foreground"
            />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder ?? t("common.select.search_placeholder")}
              className="h-9 w-full bg-transparent py-2 text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-sm text-muted-foreground">{emptyText ?? t("common.state.no_results")}</div>
            ) : (
              filtered.map((o) => {
                const isSel = o.value === value
                return (
                  <button
                    key={o.value}
                    type="button"
                    onClick={() => {
                      onValueChange(o.value)
                      setOpen(false)
                      setQuery("")
                    }}
                    className={cn(
                      "relative flex w-full cursor-default items-center gap-2.5 rounded-xl py-2 pr-8 pl-3 text-left text-sm outline-hidden select-none hover:bg-accent hover:text-accent-foreground",
                      isSel && "bg-accent/50"
                    )}
                  >
                    <span className="line-clamp-1">{o.label}</span>
                    {isSel && (
                      <span className="absolute right-2 flex size-4 items-center justify-center">
                        <HugeiconsIcon icon={Tick02Icon} strokeWidth={2} className="size-4" />
                      </span>
                    )}
                  </button>
                )
              })
            )}
          </div>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
